import React, { useState, useEffect, useMemo } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Button from "../../../components/ui/Button"
import { Avatar, AvatarImage, AvatarFallback } from "../../../components/ui/Avatar"
import { Card, CardContent } from "../../../components/ui/card"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../components/ui/table"
import { Badge } from "../../../components/ui/badge"
import { listOrgLearners, computeLearnerMetrics } from "../../../utils/learnerHelpers"
import LearnerProfileDrawer from "./LearnerProfileDrawer"

const formatRelativeTime = (ms) => {
  if (!ms) return "Never"
  const days = Math.floor((Date.now() - ms) / 86400000)
  if (days <= 0) return "Today"
  if (days === 1) return "Yesterday"
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

const initials = (name, email) => {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/)
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?"
  }
  return (email?.[0] || "?").toUpperCase()
}

const STATUS_BADGE = {
  active: { variant: "success", label: "Active" },
  "at-risk": { variant: "warning", label: "At Risk" },
  inactive: { variant: "secondary", label: "Inactive" },
}

// Small local metric card so this panel doesn't need to pull in the full
// KPIRow carousel machinery for a 5-up static strip.
const MetricTile = ({ label, value }) => (
  <div className="p-4 rounded-2xl bg-foreground/[0.02] border border-foreground/[0.06]">
    <p className="text-sm text-muted-foreground mb-1">{label}</p>
    <p className="text-2xl font-semibold text-foreground">{value}</p>
  </div>
)

const LearnersPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [learners, setLearners] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [activeLearner, setActiveLearner] = useState(null)

  const loadLearners = async () => {
    setLoading(true)
    const { learners: list } = await listOrgLearners(orgId)
    setLearners(list || [])
    setLoading(false)
  }

  useEffect(() => {
    if (orgId) loadLearners()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const metrics = useMemo(() => computeLearnerMetrics(learners), [learners])

  const filteredLearners = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return learners
    return learners.filter(
      (l) =>
        l.name?.toLowerCase().includes(q) ||
        l.email?.toLowerCase().includes(q) ||
        l.nesolearnId?.toLowerCase().includes(q)
    )
  }, [learners, search])

  return (
    <div className="space-y-6 md:col-span-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-foreground">Learners</h2>
          <p className="text-sm text-muted-foreground">{metrics.total} total learners</p>
        </div>
        <Button variant="default" iconName="UserPlus" iconPosition="left">
          Invite Learners
        </Button>
      </div>

      <Input
        type="text"
        placeholder="Search by name, email, or Nesolearn ID..."
        value={search}
        onChange={(e) => setSearch(e?.target?.value)}
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <MetricTile label="Active" value={metrics.active} />
        <MetricTile label="New" value={metrics.newCount} />
        <MetricTile label="Completed" value={metrics.completed} />
        <MetricTile label="At Risk" value={metrics.atRisk} />
        <MetricTile label="Inactive" value={metrics.inactive} />
      </div>

      <Card className="p-0 dark:bg-transparent">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-muted-foreground text-sm">Loading learners...</div>
          ) : filteredLearners?.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">
              {search ? "No learners match your search." : "No learners enrolled yet."}
            </div>
          ) : (
            <Table className="border-t">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">Learner</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Courses</TableHead>
                  <TableHead>Credentials</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead className="pr-5">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLearners?.map((learner) => {
                  const badge = STATUS_BADGE[learner.status] || STATUS_BADGE.inactive
                  return (
                    <TableRow
                      key={learner.userId}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setActiveLearner(learner)}
                    >
                      <TableCell className="pl-5">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={learner.avatarUrl} alt={learner.name || learner.email} />
                            <AvatarFallback>{initials(learner.name, learner.email)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {learner.name || "Unnamed Learner"}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {learner.email || learner.userId}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 w-28">
                          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full"
                              style={{ width: `${learner.avgProgress}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">{learner.avgProgress}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{learner.coursesCount}</TableCell>
                      <TableCell>{learner.credentialsCount}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatRelativeTime(learner.lastActivityMs)}
                      </TableCell>
                      <TableCell className="pr-5">
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

            <LearnerProfileDrawer
        learner={activeLearner}
        organization={organization}
        open={!!activeLearner}
        onClose={() => setActiveLearner(null)}
        onLearnerUpdated={loadLearners}
      />
    </div>
  )
}

export default LearnersPanel